import { useEffect, useState } from "react";
import {  Button, Table, Modal, Form, Row, Col } from "react-bootstrap";
//Alerts
import ErrorAlert from "../../Alerts/ErrorAlert";
import SuccessAlert from "../../Alerts/SuccessAlert";
//API
import { EventsFetchAPI } from "../../../api/Events";
import { GroupsFechtAPI } from "../../../api/Groups";


const AssignProductsToRuleEvent = ({ show, onHide, rule, businessId, token }) => {
  // Selected products that are already assigned to the rule
  const [assigned, setAssigned] = useState([]); // [{id,name,sku}]
  // Search results
  const [filter, setFilter] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  // Load assigned products when opening
  useEffect(() => {
    if (!show || !rule?.id) return;
    (async () => {
      try {
        setLoading(true);
        // Load products already linked to this rule
        const res = await EventsFetchAPI.getRulesEventProducts(rule.id, token);
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.items || []);
        setAssigned(list.map(p => ({ group_id: p.group_id, name: p.name, price: p.price })));
      } catch (e) {
        setAssigned([]);
        //ErrorAlert("No se pudo cargar los productos asignados", "Reglas del evento", "warning");
      } finally {
        setLoading(false);
      }
    })();
  }, [show, rule?.id, token]);

  // Search products
  const doSearch = async (_page = 1, _filter = filter) => {
    if (!businessId) return;
    try {
      setLoading(true);
      const res = await GroupsFechtAPI.searchProducts(businessId, _filter?.trim() || "", _page, token);
      const items = res?.data?.groups || res?.data || [];
      setResults(items.map(p => ({ group_id: p.group_id, name: p.name, price: p.price })));
      setTotal(Number(res?.data?.total ?? items.length));
      setPage(Number(res?.data?.page ?? _page));
    } catch (e) {
      ErrorAlert("No se pudo buscar productos", "Reglas del evento", "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) doSearch(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Add product to assigned (avoid duplicates)
  const addProduct = (prod) => {
    if (assigned.some(p => p.group_id === prod.group_id)) return;
    setAssigned(prev => [...prev, prod]);
  };

  // Remove product from assigned
  const removeProduct = (group_id) => {
    setAssigned(prev => prev.filter(p => p.group_id !== group_id));
  };

  // Persist full set
  const save = async () => {
    if (!rule?.id) return;
    try {
      setLoading(true);
      const product_ids = assigned.map(p => p.group_id);
      await EventsFetchAPI.assignProductRuleEvent(rule.id, { product_ids }, token);
      SuccessAlert("Productos asignados guardados", "Listo");
      onHide();
    } catch (e) {
      ErrorAlert(e?.response?.data?.error || "Error guardando productos", "Pick & Go", "warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Productos para: <strong>{rule?.name || `Regla #${rule?.id}`}</strong></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <h6 className="mb-2">Buscar productos</h6>
            <div className="d-flex gap-2">
              <Form.Control
                placeholder="Nombre o SKU"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doSearch(1, filter); }}
              />
              <Button variant="outline-secondary" onClick={() => doSearch(1, filter)} disabled={loading}>
                Buscar
              </Button>
            </div>

            <div className="border rounded mt-2" style={{ maxHeight: 320, overflow: 'auto' }}>
              <Table size="sm" hover className="mb-0">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Precio</th>
                    <th className="text-end">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(item => (
                    <tr key={item.group_id}>
                      <td>{item.name}</td>
                      <td className="text-muted">{item.price || '-'}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => addProduct(item)}
                          disabled={assigned.some(p => p.group_id === item.group_id)}
                        >
                          Agregar
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!results.length && (
                    <tr><td colSpan={3} className="text-center text-muted">Sin resultados</td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2">
              <div className="small text-muted">
                Página {page} • {total} resultados
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="light" disabled={page <= 1} onClick={() => doSearch(page - 1)}>Anterior</Button>
                <Button size="sm" variant="light" disabled={(page * pageSize) >= total} onClick={() => doSearch(page + 1)}>Siguiente</Button>
              </div>
            </div>
          </Col>

          <Col md={6}>
            <h6 className="mb-2">Asignados a la regla</h6>
            <div className="border rounded p-2" style={{ maxHeight: 400, overflow: 'auto' }}>
              {!assigned.length && <div className="text-muted small">Aún no hay productos asignados.</div>}
              {assigned.map(p => (
                <div key={p.group_id} className="d-flex justify-content-between align-items-center border rounded px-2 py-1 mb-2 bg-light">
                  <div>
                    <div className="fw-semibold">{p.name}</div>
                    <div className="small text-muted">{p.sku || '—'}</div>
                  </div>
                  <Button size="sm" variant="outline-danger" onClick={() => removeProduct(p.group_id)} title="Quitar">
                    quitar
                  </Button>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Cancelar</Button>
        <Button variant="primary" onClick={save} disabled={loading}>Guardar</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AssignProductsToRuleEvent;